import React, { useState, useEffect } from 'react';
import { Shield, Plus, Edit2, Trash2, Lock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useAdminAuth } from '../context/AdminAuthContext';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/utils';

const AdminUserRoles = () => {
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState({});
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [form, setForm] = useState({ name: '', permissions: {} });

  const { api, hasPermission } = useAdminAuth();

  useEffect(() => {
    fetchRoles();
    fetchModules();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/roles/');
      setRoles(response.data.roles || []);
    } catch (error) {
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  const fetchModules = async () => {
    try {
      const response = await api.get('/api/admin/roles/modules');
      setModules(response.data.modules || {});
    } catch (error) {
      console.error('Failed to load modules', error);
    }
  };

  const openAddDialog = () => {
    setEditingRole(null);
    setForm({ name: '', permissions: {} });
    setShowDialog(true);
  };

  const openEditDialog = (role) => {
    setEditingRole(role);
    setForm({
      name: role.name || '',
      permissions: role.permissions || {}
    });
    setShowDialog(true);
  };

  const handlePermissionChange = (module, action, checked) => {
    setForm(prev => {
      const modPerms = [...(prev.permissions[module] || [])];
      if (checked) {
        if (!modPerms.includes(action)) modPerms.push(action);
      } else {
        const idx = modPerms.indexOf(action);
        if (idx >= 0) modPerms.splice(idx, 1);
      }
      const perms = { ...prev.permissions };
      if (modPerms.length) perms[module] = modPerms;
      else delete perms[module];
      return { ...prev, permissions: perms };
    });
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error('Role name is required');
      return;
    }
    try {
      if (editingRole) {
        await api.put(`/api/admin/roles/${editingRole.role_id}`, {
          name: form.name,
          permissions: form.permissions
        });
        toast.success('Role updated');
      } else {
        await api.post('/api/admin/roles/', {
          name: form.name,
          permissions: form.permissions
        });
        toast.success('Role created');
      }
      setShowDialog(false);
      fetchRoles();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to save role'));
    }
  };

  const handleDelete = async (role) => {
    if (!window.confirm(`Delete role "${role.name}"?`)) return;
    try {
      await api.delete(`/api/admin/roles/${role.role_id}`);
      toast.success('Role deleted');
      fetchRoles();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to delete role'));
    }
  };

  const getPermissionsSummary = (permissions) => {
    if (!permissions || !Object.keys(permissions).length) return 'No permissions';
    const mods = Object.keys(permissions);
    return `${mods.length} module(s): ${mods.slice(0, 3).join(', ')}${mods.length > 3 ? '...' : ''}`;
  };

  return (
    <div className="space-y-6" data-testid="admin-user-roles">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Roles</h1>
          <p className="text-gray-500">Manage roles and module-wise permissions</p>
        </div>
        {hasPermission('roles', 'create') && (
          <Button onClick={openAddDialog} className="bg-blue-600 hover:bg-blue-700 text-white">
            <Plus size={16} className="mr-2" />
            Add Role
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-700">Role</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-700">Permissions</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-700">Type</th>
                  <th className="text-right px-6 py-4 text-sm font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {roles.map((role) => (
                  <tr key={role.role_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Shield size={18} className="text-blue-500" />
                        <span className="font-medium">{role.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {getPermissionsSummary(role.permissions)}
                    </td>
                    <td className="px-6 py-4">
                      {role.is_system ? (
                        <Badge variant="outline" className="text-amber-600">
                          <Lock size={12} className="mr-1" />
                          System
                        </Badge>
                      ) : (
                        <Badge variant="outline">Custom</Badge>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {hasPermission('roles', 'edit') && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(role)}
                          className="mr-2"
                        >
                          <Edit2 size={14} />
                        </Button>
                      )}
                      {hasPermission('roles', 'delete') && !role.is_system && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(role)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRole ? 'Edit Role' : 'Add Role'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Role Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Content Manager"
              />
            </div>
            <div>
              <Label className="mb-3 block">Permissions (module-wise)</Label>
              <div className="space-y-4 border rounded-lg p-4 max-h-80 overflow-y-auto">
                {Object.entries(modules).map(([moduleKey, actions]) => (
                  <div key={moduleKey} className="border-b pb-3 last:border-0">
                    <p className="font-medium text-sm mb-2 capitalize">{moduleKey.replace('_', ' ')}</p>
                    <div className="flex flex-wrap gap-2">
                      {(actions || []).map((action) => (
                        <label
                          key={action}
                          className="flex items-center gap-1.5 text-sm cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={(form.permissions[moduleKey] || []).includes(action)}
                            onChange={(e) => handlePermissionChange(moduleKey, action, e.target.checked)}
                          />
                          <span>{action}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit}>
              {editingRole ? 'Update' : 'Create'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUserRoles;
